import { useState, useEffect, useCallback } from 'react';
import { Bomb, Flag, RotateCcw, Trophy } from 'lucide-react';

const SIZE = 9;
const MINES = 10;

interface Cell {
  mine: boolean;
  open: boolean;
  flag: boolean;
  adj: number;
}

function buildBoard(): Cell[][] {
  const cells: Cell[][] = Array.from({ length: SIZE }, () =>
    Array.from({ length: SIZE }, () => ({ mine: false, open: false, flag: false, adj: 0 }))
  );
  let placed = 0;
  while (placed < MINES) {
    const r = Math.floor(Math.random() * SIZE);
    const c = Math.floor(Math.random() * SIZE);
    if (!cells[r][c].mine) {
      cells[r][c].mine = true;
      placed++;
    }
  }
  const dirs = [-1, 0, 1];
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      if (cells[r][c].mine) continue;
      let n = 0;
      for (const dr of dirs) {
        for (const dc of dirs) {
          if (dr === 0 && dc === 0) continue;
          const nr = r + dr;
          const nc = c + dc;
          if (nr >= 0 && nr < SIZE && nc >= 0 && nc < SIZE && cells[nr][nc].mine) n++;
        }
      }
      cells[r][c].adj = n;
    }
  }
  return cells;
}

const NUM_COLORS = ['', 'text-blue-400', 'text-green-400', 'text-red-400', 'text-purple-400', 'text-amber-400', 'text-cyan-400', 'text-pink-400', 'text-slate-300'];

export function MinesweeperApp() {
  const [board, setBoard] = useState<Cell[][]>(() => buildBoard());
  const [status, setStatus] = useState<'playing' | 'won' | 'lost'>('playing');
  const [seconds, setSeconds] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!started || status !== 'playing') return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [started, status]);

  const reset = useCallback(() => {
    setBoard(buildBoard());
    setStatus('playing');
    setSeconds(0);
    setStarted(false);
  }, []);

  const flagsUsed = board.flat().filter((c) => c.flag).length;

  const reveal = (r: number, c: number) => {
    if (status !== 'playing') return;
    const cell = board[r][c];
    if (cell.open || cell.flag) return;
    setStarted(true);

    const next = board.map((row) => row.map((x) => ({ ...x })));
    if (next[r][c].mine) {
      next.forEach((row) => row.forEach((x) => { if (x.mine) x.open = true; }));
      setBoard(next);
      setStatus('lost');
      return;
    }
    const stack: [number, number][] = [[r, c]];
    while (stack.length > 0) {
      const [cr, cc] = stack.pop()!;
      const cur = next[cr][cc];
      if (cur.open || cur.flag) continue;
      cur.open = true;
      if (cur.adj === 0 && !cur.mine) {
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const nr = cr + dr;
            const nc = cc + dc;
            if (nr >= 0 && nr < SIZE && nc >= 0 && nc < SIZE && !next[nr][nc].open) {
              stack.push([nr, nc]);
            }
          }
        }
      }
    }
    const won = next.flat().every((x) => x.open || x.mine);
    setBoard(next);
    if (won) setStatus('won');
  };

  const toggleFlag = (r: number, c: number) => {
    if (status !== 'playing') return;
    const cell = board[r][c];
    if (cell.open) return;
    const next = board.map((row) => row.map((x) => ({ ...x })));
    next[r][c].flag = !next[r][c].flag;
    setBoard(next);
    setStarted(true);
  };

  return (
    <div className="h-full flex flex-col bg-desktop-surface2 select-none">
      <div className="flex items-center justify-between px-3 py-2 bg-desktop-surface border-b border-desktop-border shrink-0">
        <div className="flex items-center gap-1.5 text-xs text-desktop-text">
          <Bomb className="w-3.5 h-3.5 text-red-400" />
          <span className="font-mono font-semibold">{String(MINES - flagsUsed).padStart(2, '0')}</span>
        </div>
        <button
          onClick={reset}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-desktop-surface2 hover:bg-desktop-hover transition-colors"
          title="New game"
        >
          {status === 'lost' ? (
            <span className="text-base">😵</span>
          ) : status === 'won' ? (
            <Trophy className="w-4 h-4 text-amber-400" />
          ) : (
            <RotateCcw className="w-4 h-4 text-desktop-text/70" />
          )}
        </button>
        <div className="text-xs font-mono text-desktop-text w-10 text-right">
          {String(seconds).padStart(3, '0')}
        </div>
      </div>
      {status !== 'playing' && (
        <div className={`text-center text-xs py-1.5 font-medium shrink-0 ${status === 'won' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
          {status === 'won' ? `You win! Finished in ${seconds}s` : 'Boom! Click reset to try again'}
        </div>
      )}
      <div className="flex-1 flex items-center justify-center p-3 overflow-auto">
        <div
          className="grid gap-0.5"
          style={{ gridTemplateColumns: `repeat(${SIZE}, minmax(0, 1fr))` }}
          onContextMenu={(e) => e.preventDefault()}
        >
          {board.map((row, r) =>
            row.map((cell, c) => (
              <button
                key={`${r}-${c}`}
                onClick={() => reveal(r, c)}
                onContextMenu={(e) => { e.preventDefault(); toggleFlag(r, c); }}
                className={`w-7 h-7 flex items-center justify-center text-sm font-bold rounded-[4px] transition-colors ${
                  cell.open
                    ? cell.mine
                      ? 'bg-red-600/70'
                      : 'bg-desktop-surface'
                    : 'bg-desktop-hover hover:bg-desktop-border'
                }`}
              >
                {cell.open ? (
                  cell.mine ? (
                    <Bomb className="w-3.5 h-3.5 text-white" />
                  ) : cell.adj > 0 ? (
                    <span className={NUM_COLORS[cell.adj]}>{cell.adj}</span>
                  ) : null
                ) : cell.flag ? (
                  <Flag className="w-3.5 h-3.5 text-amber-400" />
                ) : null}
              </button>
            ))
          )}
        </div>
      </div>
      <div className="px-3 py-1.5 text-[10px] text-desktop-text/40 border-t border-desktop-border shrink-0">
        Left-click: reveal · Right-click: flag
      </div>
    </div>
  );
}
